import React, { useState } from "react";
import { Download, Trash2, Maximize2, X, Images } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Overline } from "../common/Panel";
import { toast } from "sonner";

function Lightbox({ url, onClose }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20">
        <X size={16} />
      </button>
      <img src={url} alt="Snapshot fullscreen" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

export default function GalleryTab() {
  const { snapshots, deleteSnapshot, clearSnapshots } = useApp();
  const [lightbox, setLightbox] = useState(null);

  const handleDelete = (id) => {
    deleteSnapshot(id);
    toast.info("Snapshot removed");
  };

  const handleClear = () => {
    if (snapshots.length === 0) return;
    clearSnapshots();
    toast.info("Gallery cleared");
  };

  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })
      + " " + d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* Header */}
      <div className="flex items-start sm:items-center justify-between flex-wrap gap-2 sm:gap-3">
        <div>
          <Overline>TTGO T-Camera Snapshots</Overline>
          <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight mt-1">Gallery</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] sm:text-xs font-mono text-[var(--t2)]">
            {snapshots.length} photo{snapshots.length !== 1 ? "s" : ""}
          </span>
          {snapshots.length > 0 && (
            <button onClick={handleClear}
              className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md border border-[var(--danger)]/40 text-[var(--danger)] text-[10px] sm:text-xs font-medium hover:bg-[var(--danger)]/10 transition-colors">
              <Trash2 size={11} className="sm:w-3.5 sm:h-3.5" /> Clear all
            </button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {snapshots.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[var(--bd)] py-12 sm:py-20 px-4 text-center">
          <Images size={32} className="text-[var(--t2)] opacity-30 mb-3 sm:w-12 sm:h-12 sm:mb-4" />
          <p className="text-xs sm:text-sm font-medium text-[var(--t2)] leading-snug">No snapshots yet</p>
          <p className="text-[10px] sm:text-xs text-[var(--t2)] mt-1.5 font-mono leading-relaxed max-w-[15rem] sm:max-w-xs">
            Go to Camera tab, take a snapshot, then click <strong>Save to Gallery</strong>
          </p>
        </div>
      ) : (
        /* Grid */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {snapshots.map((snap) => (
            <div key={snap.id} className="group rounded-lg sm:rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors">

              {/* Thumbnail */}
              <div className="aspect-video overflow-hidden bg-[var(--surface2)] relative cursor-pointer" onClick={() => setLightbox(snap.url)}>
                <img src={snap.url} alt="TTGO snapshot" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Maximize2 size={18} className="text-white sm:w-5 sm:h-5" />
                </div>
              </div>

              {/* Meta + actions */}
              <div className="p-1.5 sm:p-2.5">
                <p className="text-[9px] sm:text-[10px] text-[var(--t2)] font-mono truncate">{fmt(snap.ts)}</p>
                <div className="flex gap-1 sm:gap-1.5 mt-1.5 sm:mt-2">
                  <a href={snap.url} download={`ttgo-${snap.id}.jpg`}
                    className="flex-1 py-1 sm:py-1.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-[9px] sm:text-xs flex items-center justify-center gap-0.5 sm:gap-1 hover:border-[var(--bd-strong)] transition-colors">
                    <Download size={10} className="sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline sm:inline">Save</span>
                  </a>
                  <button onClick={() => handleDelete(snap.id)}
                    className="flex-1 py-1 sm:py-1.5 rounded-md border border-[var(--danger)]/30 text-[var(--danger)] text-[9px] sm:text-xs flex items-center justify-center gap-0.5 sm:gap-1 hover:bg-[var(--danger)]/10 transition-colors">
                    <Trash2 size={10} className="sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline sm:inline">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
